import { makeStyles } from "tss-react/mui";
import { useRouter } from "@miplatform/hooks";
import { mdiChevronLeft } from "@mdi/js";
import { Box, Typography, useTheme } from "@mui/material";
import { MiButton } from "../MiButton/MiButton";
import { MiBuildPageHeader } from "./MiBuildPageHeader";

/**
 * `MiDetailPageHeader` is a specialized header component designed for detail pages.
 * It wraps `MiBuildPageHeader` and adds specific functionality like a back button,
 * subtitle rendering, and custom styling appropriate for detail views.
 *
 * @component
 *
 * @param {Object} props - The component props.
 * @param {string} [props.titleIcon=mdiChevronLeft] - The SVG path for the back button icon. Defaults to a left chevron.
 * @param {Object} [props.style={}] - Custom styles applied to the root header container. Merged with default background color.
 * @param {Object} [props.titleContainerStyle={}] - Custom styles applied to the title container. Merged with default margin.
 * @param {string[]} [props.subTitles=[]] - An array of strings to display as subtitles next to the main title, separated by dividers.
 * @param {Function} [props.customOnBack] - A custom callback function to execute when the back button is clicked. If not provided, it attempts to use the router to navigate back.
 * @param {React.ReactNode} [props.leftComponent] - A custom component to render on the left side (replacing the default subtitle rendering).
 * @param {string|React.ReactNode} [props.title] - The main title of the page.
 * @param {boolean} [props.loading] - Whether the header is in a loading state.
 * @param {React.ReactNode} [props.actionComponent] - A custom component to render in the action area of the header.
 * @param {string} [props.search] - The current search value for the search input.
 * @param {Function} [props.onHandleSearch] - Callback function to handle changes in the search input.
 * @param {string} [props.searchPlaceholder] - Placeholder text for the search input.
 * @param {boolean} [props.hideOnBack] - If true, hides the back button.
 * @param {Object} [props...rest] - Any other props passed down to the underlying `MiBuildPageHeader` component.
 *
 * @returns {JSX.Element} The rendered detail page header.
 *
 * @example
 * // Basic usage with title and subtitles
 * <MiDetailPageHeader
 *   title="User Details"
 *   subTitles={["ID: 123", "Status: Active"]}
 * />
 *
 * @example
 * // Usage with custom back action
 * <MiDetailPageHeader
 *   title="Edit Profile"
 *   customOnBack={() => console.log("Going back...")}
 * />
 */
export function MiDetailPageHeader({
  titleIcon = mdiChevronLeft,
  style = {},
  titleContainerStyle = {},
  subTitles = [],
  customOnBack,
  leftComponent,
  actionComponent,
  search,
  onHandleSearch,
  searchPlaceholder,
  hideOnBack,
  ...props
} = {}) {
  const theme = useTheme();
  const router = useRouter();
  const { classes } = useStyles();

  const handleBackClick = () => {
    if (!customOnBack && router.location.key !== "default") {
      return router.navigate(-1);
    }
    return customOnBack();
  };

  const renderHeaderIcon = () => (
    <MiButton
      onClick={handleBackClick}
      variant="contained"
      size="small"
      startIcon={{ path: titleIcon }}
      className={classes.backBtn}
      data-testid="header-go-back-icon"
    />
  );

  const renderLeftComponent = () => {
    if (leftComponent) return leftComponent;
    return (
      <Box className={classes.leftComponentContainer}>
        {subTitles?.map((title, index) => (
          <Box className={classes.subtitleWithDivider} key={`title-${index}`}>
            <Typography>{title}</Typography>
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <MiBuildPageHeader
      {...props}
      hasDivider
      leftComponent={renderLeftComponent()}
      actionComponent={actionComponent}
      titleIcon={!hideOnBack && renderHeaderIcon()}
      title={props.title}
      loading={props.loading}
      titleContainerStyle={{
        marginLeft: hideOnBack ? 0 : 40,
        ...titleContainerStyle,
      }}
      search={search}
      onHandleSearch={onHandleSearch}
      searchPlaceholder={searchPlaceholder}
      style={{ backgroundColor: theme.palette.primary.light, ...style }}
    />
  );
}

const useStyles = makeStyles()((theme) => ({
  backBtn: {
    backgroundColor: theme.palette.primary.main,
    height: "100%",
    width: 30,
    position: "absolute",
    top: 0,
    left: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 0,
  },
  leftComponentContainer: {
    display: "flex",
    flexDirection: "row",
  },
  subtitleWithDivider: {
    paddingLeft: 16,
    borderLeft: `1px solid ${theme.palette.grey.main}`,
    marginLeft: 16,
    fontSize: theme.fontSize.medium,
    color: theme.palette.grey.main,
  },
}));
